import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { DataSet, SerializationLevel } from '@hidat/huijs-api';
import { AnyHash, Uuid } from '@hidat/huijs-common';
import { PlayerApiService } from './api/player-api.service';
import { MixConfiguration, PlayerSession, UserHash } from '../models';
import { PlayerAutoPlayMode, SteeringMode } from '@splixer/common';
import { StationManagerService } from './station-manager.service';

/**
 * Session Manager
 * Provides an abstraction layer to the session api.
 * Eventually we will extend to include the session singleton, so it can become the source of truth for the session.
 */
@Injectable({ providedIn: 'root' })
export class SessionsManagerService {
  private api = inject(PlayerApiService);
  private stationManager = inject(StationManagerService);

  loadLink(linkCode: string, instanceId: string): Observable<DataSet<AnyHash>> {
    const q = {
      link_code: linkCode,
      instance_id: instanceId,
    };
    return this.api.sessions.put(
      ['load_link'],
      q,
      SerializationLevel.None,
    ) as Observable<DataSet<AnyHash>>;
  }

  //@TODO: Does this return a PlayerSession?
  knock(
    accessCode: string,
    userInfo: UserHash,
    stationCode: string,
    instanceId: string,
  ): Observable<DataSet<AnyHash>> {
    const payload = {
      access_code: accessCode,
      user: userInfo,
      station_code: stationCode,
      instance_id: instanceId,
    };
    return this.api.sessions.post(
      ['knock'],
      payload,
      SerializationLevel.None,
    ) as Observable<DataSet<AnyHash>>;
  }

  /**
   * Helper function to figure out what includes we need to get when we ask for new content
   * @protected
   */
  protected contentIncludes(): string {
    let includes =
      'items_to_play,current_path,current_state,skip_status,instance';
    if (this.stationManager.includeRelatedContent) {
      includes += ',related_content';
    }
    return includes;
  }

  createSession(
    stationCode: string,
    instanceID: string | undefined,
    mixConfig?: MixConfiguration,
    mixUid?: string,
    userMixId?: number,
  ): Observable<PlayerSession | undefined> {
    let tz = '';
    let offset = 0;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      offset = -new Date().getTimezoneOffset();
    } catch (e) {
      console.error('Exception while getting tz info');
    }
    const payload = {
      instance_id: instanceID,
      station_code: stationCode,
      tz: tz,
      tz_offset: offset,
      update: {
        mix_config: mixConfig,
        mix_uid: mixUid,
        user_mix_id: userMixId,
      },
      include: this.contentIncludes(),
    };
    return this.api.sessions.postOne(
      [],
      payload,
      SerializationLevel.Output,
    ) as Observable<PlayerSession>;
  }

  updateSession(
    instanceID: string,
    sessionId: string,
    mixConfig?: MixConfiguration,
    mixUid?: string,
    userMixId?: number,
  ): Observable<PlayerSession> {
    // May need to add 'userSettings' as a separate entity at some point.
    let tz = '';
    let offset = 0;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      offset = -new Date().getTimezoneOffset();
    } catch (e) {
      console.error('Exception while getting tz info');
    }
    const payload = {
      instance_id: instanceID,
      tz: tz,
      tz_offset: offset,
      update: {
        mix_config: mixConfig,
        mix_uid: mixUid,
        user_mix_id: userMixId,
      },
      include: this.contentIncludes(),
    };

    return this.api.sessions.putOne(
      [sessionId],
      payload,
      SerializationLevel.Output,
    ) as Observable<PlayerSession>;
  }

  /**
   * Goto Next Mix
   * @param instanceID
   * @param sessionId
   * @param autoPlayMode
   */
  gotoNextMix(
    instanceID: string,
    sessionId: string,
    autoPlayMode: PlayerAutoPlayMode,
  ): Observable<PlayerSession> {
    let tz = '';
    let offset = 0;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      offset = -new Date().getTimezoneOffset();
    } catch (e) {
      console.error('Exception while getting tz info');
    }
    const payload = {
      instance_id: instanceID,
      tz: tz,
      tz_offset: offset,
      auto_mode: autoPlayMode,
      include: this.contentIncludes(),
    };

    return this.api.sessions.putOne(
      [sessionId, 'goto_next_mix'],
      payload,
      SerializationLevel.Output,
    ) as Observable<PlayerSession>;
  }

  /**
   * Returns an array of alternate paths.
   * This will also create and update the session if it does not currently exist, or has expired.
   * @param steeringMode
   * @param count
   * @param instanceId
   * @param sessionId
   */
  getAltPaths(
    steeringMode: SteeringMode,
    count: number,
    stationCode: string,
    instanceId?: string,
    sessionId?: string,
  ): Observable<PlayerSession | undefined> {
    let tz = '';
    let offset = 0;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      offset = -new Date().getTimezoneOffset();
    } catch (e) {
      console.error('Exception while getting tz info');
    }
    const payload = {
      instance_id: instanceId,
      station_code: stationCode,
      tz: tz,
      tz_offset: offset,
      session_id: sessionId,
      steering_mode: steeringMode,
      count: count,
      include: 'alt_paths',
    };
    return this.api.sessions.postOne(
      ['alt_paths'],
      payload,
      SerializationLevel.Output,
    ) as Observable<PlayerSession | undefined>;
  }

  /**
   * Play the given mix, specified by the given UID (not id....)
   * @param instanceID
   * @param sessionId
   * @param stationCode
   * @param mixUid
   */
  playMix(
    instanceID: string,
    sessionId: string | undefined,
    stationCode: string,
    mixUid: string | undefined,
  ): Observable<PlayerSession> {
    let tz = '';
    let offset = 0;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      offset = -new Date().getTimezoneOffset();
    } catch (e) {
      console.error('Exception while getting tz info');
    }
    const payload = {
      instance_id: instanceID,
      station_code: stationCode,
      tz: tz,
      tz_offset: offset,
      update: {
        mix_uid: mixUid,
      },
      include: this.contentIncludes(),
    };

    if (sessionId) {
      return this.api.sessions
        .put([sessionId], payload, SerializationLevel.Output)
        .pipe(
          map((ds) => {
            return ds.firstItem() as PlayerSession;
          }),
        );
    }
    return this.api.sessions.postOne(
      [],
      payload,
      SerializationLevel.Output,
    ) as Observable<PlayerSession>;
  }

  /**
   * Load Session
   * Attempts to load the current session for the given instance.  If the session has expired, a new one will be returned.
   * @param instanceID
   * @param sessionId
   * @param getItemsToPlay
   */
  loadSession(
    instanceID: string,
    sessionId: string,
    getItemsToPlay = false,
  ): Observable<PlayerSession> {
    let include = 'current_path,current_state,skip_status,instance,';
    if (getItemsToPlay) {
      include += ',items_to_play';
    }

    const payload = {
      instance_id: instanceID,
      id: sessionId,
      schedule: getItemsToPlay,
      include: include,
    };
    return this.api.sessions
      .put(['load_session'], payload, SerializationLevel.Output)
      .pipe(
        map((ds) => {
          return ds.firstItem() as PlayerSession;
        }),
      );
  }

  saveMix(
    sessionId: string,
    mixName: string,
    overwriteExisting: boolean,
  ): Observable<DataSet<AnyHash>> {
    const payload = {
      mix_name: mixName,
      overwrite_existing: overwriteExisting,
      include: 'current_path,current_state',
    };

    return this.api.sessions.put(
      [sessionId, 'save_mix'],
      payload,
      SerializationLevel.None,
    ) as Observable<DataSet<AnyHash>>;
  }

  getItemsToPlay(
    instanceID: string | undefined,
    sessionId: string,
  ): Observable<PlayerSession> {
    const payload = {
      instance_id: instanceID,
      include: 'items_to_play',
    };
    return this.api.sessions
      .put([sessionId, 'get_next_items'], payload, SerializationLevel.Output)
      .pipe(
        map((ds) => {
          return ds.firstItem() as PlayerSession;
        }),
      );
  }

  public gotoScheduledItem(
    instanceID: string | undefined,
    sessionId: string,
    itemId: Uuid,
  ): Observable<PlayerSession> {
    const payload = {
      scheduling_item_id: itemId,
      instance_id: instanceID,
      include: 'items_to_play',
    };
    return this.api.sessions
      .put([sessionId, 'goto_item'], payload, SerializationLevel.Output)
      .pipe(
        map((ds) => {
          return ds.firstItem() as PlayerSession;
        }),
      );
  }

  markContentChanged(
    sessionId: string,
    contentToPlayId: string | undefined,
    contentPlayedId: string | undefined,
    includeAltPaths = false,
  ): Observable<DataSet<PlayerSession>> {
    const payload = {
      content_to_play_id: contentToPlayId,
      content_played_id: contentPlayedId,
      get_alt_paths: includeAltPaths,
    };
    return this.api.sessions.put(
      [sessionId, 'content_changed'],
      payload,
      SerializationLevel.Output,
    ) as Observable<DataSet<PlayerSession>>;
  }

  /**
   * Track Listening Time
   * @param sessionId
   * @param secondsListened
   */
  trackListeningTime(
    sessionId: string,
    secondsListened: number,
  ): Observable<DataSet<AnyHash>> {
    const payload = {
      seconds_listened: secondsListened,
    };
    return this.api.sessions.put(
      [sessionId, 'listen_time'],
      payload,
      SerializationLevel.None,
    ) as Observable<DataSet<AnyHash>>;
  }

  contentSkipped(
    sessionId: string,
    contentLogId: string | undefined,
  ): Observable<DataSet<PlayerSession>> {
    const payload = {
      content_log_id: contentLogId,
      include: 'skip_status',
    };
    return this.api.sessions.put(
      [sessionId, 'content_skipped'],
      payload,
      SerializationLevel.Output,
    ) as Observable<DataSet<PlayerSession>>;
  }

  createUser(instanceId: string, user: UserHash): Observable<DataSet<AnyHash>> {
    const payload = {
      instance_id: instanceId,
      user: user,
    };
    return this.api.sessions.post(
      [],
      payload,
      SerializationLevel.None,
    ) as Observable<DataSet<AnyHash>>;
  }

  /**
   * Saves the current mix to the given name.
   * @returns {Promise<any>}
   */
  /* @TODO Figure out types
  saveMix(mixName: string, overwriteExisting = true): Observable<AnyHash> {
    this.eventTracker.mixSaved();
    const p = this.api.sessions.saveMix(this.deviceStore.sessionId, mixName, overwriteExisting);
    p.subscribe((results) => {
      // Add to the end of the current user mixes
      this.station.addUserMix(results.user_mix);
      // Update the current path information so we display the just saved name.
      this.addServerResults(results, false)
      return results;
    });
    return p;
  }*/
}
